import {
  FileText,
  Funnel,
  Info,
  MagnifyingGlass,
  PhoneDisconnect,
  PhoneSlash,
  Robot,
} from '@phosphor-icons/react';
import {
  endOfDay,
  formatDuration,
  intervalToDuration,
  startOfDay,
  subDays,
} from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Badge, BadgeVariant } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import { CallDetailsModal } from 'src/components/CallDetailsModal';
import { CallErrorDisplay } from 'src/components/CallErrorDisplay';
import { CallFiltersModal } from 'src/components/CallFiltersModal';
import { CallRecordingButton } from 'src/components/CallRecordingButton';
import { CallsCsvExport } from 'src/components/CallsCsvExport';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Modal } from 'src/components/Modal';
import Table from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Agent } from 'src/interfaces/agent.interface';
import { Call, CallFilters } from 'src/interfaces/call.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { CallStatus } from 'src/stubs/calls.stub';
import { cn } from 'src/utils/className';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'agent', label: 'Agent', width: '8rem', disableSorting: true },
  {
    key: 'agent_name',
    label: 'Agent Name',
    width: '8rem',
    disableSorting: true,
  },
  {
    key: 'recording_available',
    label: 'Recording',
    width: '15%',
    disableSorting: true,
  },
  { key: 'start_time', label: 'Created At', width: '15%' },
  { key: 'end_time', label: 'Duration', width: '15%', disableSorting: true },
  {
    key: 'from_number',
    label: 'From',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
  {
    key: 'to_number',
    label: 'To',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
  { key: 'status', label: 'Status', width: '10%' },
  { key: 'actions', label: '', width: '10%', disableSorting: true },
];

const agentNameCache: Record<string, string> = {};

const FLUENTS_CONTEXT_PROPERTY_PREFIX = '$fluents';

const removeFluentsPropertiesFromCall = (call: Call) => {
  const context = { ...call.context };
  Object.keys(context).forEach((key) => {
    if (key.startsWith(FLUENTS_CONTEXT_PROPERTY_PREFIX)) {
      delete context[key];
    }
  });

  return {
    ...call,
    context,
  };
};

export const Calls = () => {
  const { get, post } = useAuthedRequest();
  const notification = useNotification();

  const [agents, setAgents] = useState([]);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isEndCallModalOpen, setIsEndCallModalOpen] = useState(false);
  const [loadingEndCall, setLoadingEndCall] = useState(false);
  const [rows, setRows] = useState([]);
  const [rawCalls, setRawCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState<CallFilters>(() => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    return {
      agent: 'All Agents',
      status: 'All Statuses',
      call_id: '',
      from_number: '',
      to_number: '',
      start_time: {
        comparison: 'btwn',
        value: [start.toISOString(), end.toISOString()],
      },
      duration: undefined,
      successful_actions: [],
      transcript_keyword: '',
    };
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const getDisplayStatus = (call: Call) => {
    const statusBadgeVariant: Record<string, BadgeVariant> = {
      in_progress: 'primary',
      not_started: 'secondary',
      voicemail: 'warning',
      error: 'error',
      ended: 'success',
    };

    const isVoicemail = call.human_detection_result === 'no_human';

    const hasErrors = call.errors?.length > 0 || false;

    return (
      <div className="flex items-center">
        <Badge
          variant={hasErrors ? 'warning' : statusBadgeVariant[call.status]}
          className="flex items-center whitespace-nowrap"
        >
          {CallStatus[call.status]}
          {isVoicemail && ' (Voicemail)'}
          {hasErrors && (
            <Tooltip
              content={<CallErrorDisplay errors={call.errors} />}
              position="top"
              truncate={false}
            >
              <Info className="ml-1" size={16} />
            </Tooltip>
          )}
        </Badge>
      </div>
    );
  };

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const formattedFilters: CallFilters = {
      ...filters,
      ...(filters.agent === 'All Agents' && { agent: undefined }),
      ...((filters.status === 'All Statuses' ||
        filters.status === 'voicemail' ||
        filters.status === 'is_not_voicemail') && { status: undefined }),
      ...(filters.status === 'voicemail' && { is_voicemail: true }),
      ...(filters.status === 'is_not_voicemail' && { is_voicemail: false }),
      ...(filters.successful_actions?.length === 0 && {
        successful_actions: undefined,
      }),
      ...(filters.call_id === '' && { call_id: undefined }),
    };

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify(formattedFilters),
    };

    if (sortConfig.key) {
      queryObject.sort_column = sortConfig.key;
      queryObject.sort_desc =
        sortConfig.direction === 'desc' ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const fetchResult = await get(`/calls?${query.toString()}`);
    const processedCalls = fetchResult.items.map((call: Call) =>
      removeFluentsPropertiesFromCall(call),
    );
    setRawCalls(processedCalls);

    const uniqueAgents: (string | null)[] = Array.from(
      new Set(processedCalls.map((call: Call) => call.agent)),
    );
    await Promise.all(
      uniqueAgents.map(async (uniqueAgent) => {
        if (!!uniqueAgent && !agentNameCache[uniqueAgent]) {
          try {
            const agent = await get(`/agents/${uniqueAgent}`);
            agentNameCache[uniqueAgent] = agent.name;
          } catch {
            agentNameCache[uniqueAgent] = '(deleted)';
          }
        }
      }),
    );

    const calls = await Promise.all(
      processedCalls.map(async (call: Call) => {
        const startDate = call.start_time
          ? new Date(Number(call.start_time) * 1000)
          : null;
        const endDate = call.end_time
          ? new Date(Number(call.end_time) * 1000)
          : null;

        return {
          ...call,
          id: <CopyableField value={call.id} notification={notification} />,
          agent: (
            <CopyableField value={call.agent} notification={notification} />
          ),
          agent_name: agentNameCache[call.agent],
          recording_available: call.recording_available ? (
            <CallRecordingButton
              callId={call.id}
              recordingAvailable={call.recording_available}
            />
          ) : (
            '-'
          ),
          start_time: friendlyDateFormatter(startDate),
          end_time:
            startDate && endDate
              ? formatDuration(
                  intervalToDuration({
                    start: startDate,
                    end: endDate,
                  }),
                  { delimiter: ', ' },
                )
              : '-',
          from_number: (
            <div className="flex gap-2 items-center">
              {processPhoneNumber(call.from_number)}

              {call.is_outgoing && (
                <Tooltip content="Voice Agent" position="top">
                  <Robot className="bold w-5 h-5" />
                </Tooltip>
              )}

              {((call.is_outgoing &&
                call.stage_outcome === 'bot_disconnected') ||
                (!call.is_outgoing &&
                  call.stage_outcome === 'human_disconnected')) && (
                <Tooltip content="Ended the call" position="top">
                  <PhoneSlash className="bold w-5 h-5" />
                </Tooltip>
              )}
            </div>
          ),
          to_number: (
            <div className="flex gap-2 items-center">
              {processPhoneNumber(call.to_number)}

              {!call.is_outgoing && (
                <Tooltip content="Voice Agent" position="top">
                  <Robot className="bold w-5 h-5" />
                </Tooltip>
              )}

              {((!call.is_outgoing &&
                call.stage_outcome === 'bot_disconnected') ||
                (call.is_outgoing &&
                  call.stage_outcome === 'human_disconnected')) && (
                <Tooltip content="Ended the call" position="top">
                  <PhoneSlash className="bold w-5 h-5" />
                </Tooltip>
              )}
            </div>
          ),
          status: getDisplayStatus(call),
          actions: (
            <div className="flex justify-center items-center gap-2">
              <Tooltip content="Show call details" position="left">
                <MagnifyingGlass
                  className="w-5 h-10 cursor-pointer"
                  onClick={() => handleCallDetails(call)}
                />
              </Tooltip>

              {call.status === 'in_progress' && (
                <Tooltip
                  content={
                    call.agent_phone_number === 'web'
                      ? 'End call on web is not supported for now'
                      : 'End call'
                  }
                  position="left"
                >
                  <PhoneDisconnect
                    className={cn(
                      'w-5 h-10 cursor-pointer',
                      call.agent_phone_number === 'web'
                        ? 'opacity-75 cursor-not-allowed'
                        : '',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (call.agent_phone_number !== 'web') {
                        handleEndCall(call);
                      }
                    }}
                  />
                </Tooltip>
              )}
            </div>
          ),
        };
      }),
    );

    setRows(calls as never[]);
    setTotalItems(fetchResult.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
  }, [currentPage, filters, sortConfig, size]);

  const handlePageChange = async (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const handleApplyFilters = (newFilters: CallFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const searchAgents = useCallback(async (name: string) => {
    const queryObject: QueryObject = {
      ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    setAgents(data.items);

    return data.items.map((agent: Agent) => ({
      value: agent.id,
      label: agent.name,
    }));
  }, []);

  const handleCallDetails = (call: Call) => {
    setSelectedCall(call);
    setIsCallDetailsModalOpen(true);
  };

  const handleEndCall = (call: Call) => {
    setSelectedCall(call);
    setIsEndCallModalOpen(true);
  };

  const confirmEndCall = async () => {
    setLoadingEndCall(true);

    await post(`/calls/${selectedCall?.id}/end`, {});

    notification.success('Call ended successfully');
    fetchData(currentPage, size);
    setIsEndCallModalOpen(false);
    setSelectedCall(null);
    setLoadingEndCall(false);
  };

  return (
    <div className="flex-1">
      <Heading
        title="Calls"
        subtitle="See the calls made using your agents here"
      >
        <div className="flex">
          <Button
            className="border-none flex items-center justify-center text-neutral-500"
            variant="outlined"
            color="default"
            href="https://docs.fluents.ai/api-reference/calls"
            target="_blank"
          >
            View help doc
            <FileText className="ml-2" size={18} />
          </Button>
        </div>
      </Heading>

      <Can operation="read" subject={Subject.CALLS}>
        <ContentWrapper>
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <Button
                className="border-none flex items-center justify-center"
                color="primary"
                onClick={() => setIsFilterModalOpen(true)}
              >
                <Funnel className="mr-2" size={18} />
                Filters
              </Button>

              <div className="flex items-center gap-3">
                <CallsCsvExport filters={filters} />
              </div>
            </div>
          </div>

          <Table
            headers={headers}
            onRowClick={(index: number) => {
              const call = rawCalls[index];
              if (call) {
                handleCallDetails(call);
              }
            }}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
          />
        </ContentWrapper>

        {selectedCall && (
          <CallDetailsModal
            call={selectedCall}
            isOpen={isCallDetailsModalOpen}
            onClose={() => setIsCallDetailsModalOpen(false)}
          />
        )}

        <CallFiltersModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filters}
          onApplyFilters={handleApplyFilters}
          agents={agents}
          searchAgents={searchAgents}
          loading={loading}
        />

        <Modal
          title="End Call"
          isOpen={isEndCallModalOpen}
          onClose={() => setIsEndCallModalOpen(false)}
          actionButton={
            <Button onClick={confirmEndCall} disabled={loadingEndCall}>
              End Call
            </Button>
          }
          className="w-96"
        >
          <div className="text-center">
            Are you sure you want to end this call?
          </div>
        </Modal>
      </Can>
    </div>
  );
};
